<template>
  <div
    class="payment-details-card"
    :class="paymentCardStyle"
    tabIndex="0"
    @click="emit('openModal')"
    @keyup.enter="emit('openModal')"
    data-test="payment-details-card"
  >
    <div class="text-container">
      <h3 class="header">Payment Details</h3>
      <a v-if="canEdit" class="edit" to="#">Edit</a>
    </div>
    <p class="payment-details" data-test="payment-details-masked-card">{{ paymentDetailsDisplay }}</p>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useErrorsStore } from '@/store/ErrorsStore'
import { useCheckoutStore } from '@/store/CheckoutStore'

const errorsStore = useErrorsStore()
const checkoutStore = useCheckoutStore()

const { paymentDetails } = storeToRefs(checkoutStore)
const { errors } = storeToRefs(errorsStore)

const props = defineProps({
  isUpdated: {
    type: Boolean,
    required: true
  },
  canEdit: {
    type: Boolean,
    required: true
  }
})

const lastFour = computed(() => paymentDetails.value?.lastFour)
const expMonth = computed(() => paymentDetails.value?.expMonth)
const expYear = computed(() => paymentDetails.value?.expYear)
const exp = computed(() => (expMonth?.value && expYear?.value ? `${expMonth?.value}/${expYear?.value}` : ''))

const hasValidPaymentMethod = computed(() => {
  const type = paymentDetails.value?.type
  if (!type || !supportedPaymentMethods.value[type]) return false
  if (type === 'credit_card') return isValidCreditCard.value
  return true
})

const paymentCardStyle = computed(() => {
  if (!hasValidPaymentMethod.value || errors.value.length) {
    return 'error'
  } else if (!props.canEdit) {
    return 'disabled'
  } else if (props.isUpdated) {
    return 'updated'
  }
  return ''
})

const supportedPaymentMethods = computed((): Record<string, string> => ({
  paypal: 'PayPal',
  cashapp: 'Cash App Pay',
  credit_card: `**** **** **** ${lastFour.value} ${exp.value}`
}))

const paymentDetailsDisplay = computed(() => {
  return supportedPaymentMethods.value[paymentDetails.value?.type as string] || 'Please verify payment information.'
})

const emit = defineEmits(['openModal'])

const isValidCreditCard = computed(() => {
  if (!expMonth.value || !expYear.value) {
    return false
  }

  const currentMonthYear = new Date()

  // Update to the last day of the current month
  currentMonthYear.setDate(1) // Set to the first day of the month
  currentMonthYear.setMonth(currentMonthYear.getMonth() + 1) // Move to the next month
  currentMonthYear.setDate(0) // Go back one day to get the last day of the current month

  // // CC expires midnight the first date of the next month
  const expirationDate = new Date(expYear.value, expMonth.value, 1)

  return expirationDate >= currentMonthYear
})
</script>
<style lang="scss" scoped>
.payment-details-card {
  width: 100%;
  margin: $spacer-2 0 $spacer-4 0;
  border: solid 2px $gray30;
  border-radius: 12px;
  padding: $spacer-4;
  position: relative;
  cursor: pointer;

  &:hover {
    h3 {
      text-decoration: underline;
    }
  }

  .text-container {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .header {
    color: var(--color-text-primary);
    margin-bottom: 0;
  }

  .payment-details {
    margin-bottom: 0;
  }

  @include media-breakpoint(md) {
    min-width: 318px;
  }

  &.error {
    border-color: $red50;
  }

  &.updated {
    border-color: var(--color-accent-primary);
  }

  &.disabled {
    cursor: auto;

    h3,
    h3:hover {
      text-decoration: none;
    }
  }

  a {
    height: fit-content;
    text-decoration: none;
    font-size: $fs_1;
    padding: 6px 12px;
    border: 1px solid $gray5;
    border-radius: 24px;
    color: $gray5;
  }
}
</style>
